import React, { RefObject, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import ym from 'react-yandex-metrika';

import { Collapse as CollapseIcon, Expand as ExpandIcon } from 'education-icons';
import { Checkbox, Switch, Text } from 'platform-components';
import { CHECKBOX_TYPES } from 'platform-components/constants';
import { isCodingProblem } from 'platform-components/src/components/coding-problem/utils';

import { SCROLL_CONTROLS_MODE } from 'common.components/hooks';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { LessonStore } from 'common.components/lesson/typings';
import { TeacherDetectPasteTumbler, useIsShowPastesAvailable } from 'common.components/teacher-detect-pastes';
import { ReduxStickersStore } from 'store/stickers/typings';
import { getTeamIdFromPath } from 'utils/path';

import { resultsActions } from '../actions';
import * as selectors from '../selectors';
import { SWITCH_TYPE, SwitchYmParams, LessonResultsStore } from '../typings';

import './results__controls.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface OwnProps {
    scrollControlsMode: SCROLL_CONTROLS_MODE;
    showStickersPopup?: (ref: RefObject<HTMLElement>) => void;
    cancelAllStickers?: () => void;
}

interface Props extends StoreProps, DispatchProps, OwnProps {}

// eslint-disable-next-line complexity
const ResultsControls = (props: Props) => {
    const {
        lesson,
        clessonId,
        isTimeSwitchOn,
        setShouldShowTime,
        isAttemptsSwitchOn,
        setShouldShowAttempts,
        isPointsSwitchOn,
        setShouldShowPoints,
        setStatsExpanded,
        isStatsExpanded,
        shouldShowPointsSwitch,
        shouldShowAttemptsSwitch,
        scrollControlsMode,
        isEveryoneHasStickers,
        shouldShowGiveStickersToAllCheckbox,
        subject,
        showStickersPopup,
        cancelAllStickers,
    } = props;

    const ymUrlClessonModeSetted = (params: SwitchYmParams) => {
        ym('reachGoal', 'urlClessonModeSetted', {
            clessonId,
            time: isTimeSwitchOn,
            attempts: isAttemptsSwitchOn,
            points: isPointsSwitchOn,
            ...params,
        });
    };

    const timeSwitchHandler = () => {
        ymUrlClessonModeSetted({
            time: !isTimeSwitchOn,
            type: SWITCH_TYPE.TIME,
        });

        setShouldShowTime(!isTimeSwitchOn);
    };

    const attemptsSwitchHandler = () => {
        ymUrlClessonModeSetted({
            attempts: !isAttemptsSwitchOn,
            type: SWITCH_TYPE.ATTEMPTS,
        });

        setShouldShowAttempts(!isAttemptsSwitchOn);
    };

    const pointsSwitchHandler = () => {
        ymUrlClessonModeSetted({
            points: !isPointsSwitchOn,
            type: SWITCH_TYPE.POINTS,
        });

        setShouldShowPoints(!isPointsSwitchOn);
    };

    const shouldShowExpandIcon = !isStatsExpanded && scrollControlsMode !== SCROLL_CONTROLS_MODE.NONE;
    const giveStickersToAllCheckboxRef = useRef<Checkbox | null>(null);

    const showPastesAvailable = useIsShowPastesAvailable(subject);
    const hasCodingProblems = useMemo(
        () => lesson?.problems.some((problem) => isCodingProblem(problem.problem)),
        [lesson]
    );
    const showDetectPasteTumbler = showPastesAvailable && hasCodingProblems;

    return (
        <div className="results__controls">
            <div className="controls__column controls__column_left">
                <Text id="lessonView.results.show" />
                <Switch
                    checked={isTimeSwitchOn}
                    labelPosition={Switch.LABEL_POSITIONS.RIGHT}
                    mix="qa_results-time-control"
                    onChange={timeSwitchHandler}
                    theme={Switch.THEMES.LIGHT_BLUE}
                    view={Switch.VIEWS.INVERSE}
                >
                    <Text id="lessonView.results.control.time" />
                </Switch>
                {shouldShowAttemptsSwitch && (
                    <Switch
                        checked={isAttemptsSwitchOn}
                        labelPosition={Switch.LABEL_POSITIONS.RIGHT}
                        mix="qa_results-attempts-control"
                        onChange={attemptsSwitchHandler}
                        theme={Switch.THEMES.LIGHT_BLUE}
                        view={Switch.VIEWS.INVERSE}
                    >
                        <Text id="lessonView.results.control.attempts" />
                    </Switch>
                )}
                {shouldShowPointsSwitch && (
                    <Switch
                        checked={isPointsSwitchOn}
                        labelPosition={Switch.LABEL_POSITIONS.RIGHT}
                        mix="qa_results-points-control"
                        onChange={pointsSwitchHandler}
                        theme={Switch.THEMES.LIGHT_BLUE}
                        view={Switch.VIEWS.INVERSE}
                    >
                        <Text id="lessonView.results.control.points" />
                    </Switch>
                )}
                {showDetectPasteTumbler && (
                    <TeacherDetectPasteTumbler fromOrigin="results" tooltipDirection="top-end" withTagNew={true} />
                )}
            </div>
            <div className="controls__column controls__column_right">
                {shouldShowGiveStickersToAllCheckbox && (
                    <Checkbox
                        checked={isEveryoneHasStickers}
                        mix="give-stickers-to-all__checkbox"
                        onBeforeToggle={() => {
                            if (showStickersPopup && giveStickersToAllCheckboxRef.current) {
                                const checkbox = giveStickersToAllCheckboxRef.current;
                                const current = ReactDOM.findDOMNode(checkbox) as HTMLElement;
                                if (!isEveryoneHasStickers) {
                                    showStickersPopup({ current });
                                }
                            }

                            return isEveryoneHasStickers;
                        }}
                        onChange={(e) => {
                            const { checked } = e.target;
                            if (!checked && cancelAllStickers) {
                                cancelAllStickers();

                                ym('reachGoal', 'teacher_lesson_stats_delete_all_sticker', {
                                    clesson_id: clessonId,
                                });
                            }
                        }}
                        ref={giveStickersToAllCheckboxRef}
                        type={CHECKBOX_TYPES.BLUE}
                    >
                        <Text id="lessonView.results.giveStickersToAll" />
                    </Checkbox>
                )}
                {shouldShowExpandIcon && (
                    <div className="results__expand-icon">
                        <ExpandIcon onClick={() => setStatsExpanded({ isExpanded: true })} />
                    </div>
                )}
                {isStatsExpanded && (
                    <div className="results__collapse-icon">
                        <CollapseIcon onClick={() => setStatsExpanded({ isExpanded: false })} />
                    </div>
                )}
            </div>
        </div>
    );
};

type StoreType = LessonResultsStore & LessonStore & ReduxStickersStore;

const mapStateToProps = <T extends StoreType>(storeState: T) => {
    const classId = getTeamIdFromPath();

    return {
        lesson: lessonSelectors.getLesson(storeState),
        clessonId: lessonSelectors.getCLessonId(storeState),
        isAttemptsSwitchOn: selectors.getShouldShowAttemptsValue(storeState),
        isTimeSwitchOn: selectors.getShouldShowTimeValue(storeState),
        isPointsSwitchOn: selectors.getShouldShowPointsValue(storeState),
        isStatsExpanded: selectors.getIsStatsExpanded(storeState),
        shouldShowPointsSwitch: lessonSelectors.getShouldDisplayPoints(storeState),
        shouldShowAttemptsSwitch: lessonSelectors.getShouldDisplayAttempts(storeState),
        isEveryoneHasStickers: selectors.getIsEveryoneHasStickers(storeState, { classId }),
        shouldShowGiveStickersToAllCheckbox: lessonSelectors.getIsClessonMetaHasStickers(storeState),
        subject: lessonSelectors.getLessonSubject(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) =>
    bindActionCreators(
        {
            setShouldShowAttempts: resultsActions.setShouldShowAttempts,
            setShouldShowTime: resultsActions.setShouldShowTime,
            setShouldShowPoints: resultsActions.setShouldShowPoints,
            setStatsExpanded: resultsActions.setStatsExpanded,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ResultsControls);
